import { getUserById } from "@/helpers/userHelper";
import builder from "xmlbuilder";
import { XmlEntities } from "html-entities";

const WEB2PRINT_PRIORITY_MAPPING = {
  hoch: "hoch",
  mittel: "mittel",
  niedrig: "niedrig"
};

function toIsoUtcString(value) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function removeHtmlTags(value) {
  if (!value?.length) {
    return "";
  }
  const doc = new DOMParser().parseFromString(value, "text/html");
  return doc.body?.textContent || "";
}

function normalizeWhitespace(value) {
  return (value || "").replace(/\s+/g, " ").trim();
}

function decodeEntities(value) {
  const entities = new XmlEntities();
  return entities.decode(value || "");
}

function toPlainText(value) {
  return normalizeWhitespace(decodeEntities(removeHtmlTags(value)));
}

function slugifyKey(value) {
  return (value || "")
    .toString()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getIssueMeta(issue, fallbackDate) {
  const issueTitle = issue?.title || "";
  const releaseDate = issue?.release_date || fallbackDate;
  const release = releaseDate ? new Date(releaseDate) : undefined;
  const hasDate = release && !Number.isNaN(release.getTime());

  if (hasDate) {
    return {
      title: issueTitle,
      year: String(release.getUTCFullYear()),
      month: String(release.getUTCMonth() + 1).padStart(2, "0")
    };
  }

  const titleMatch = issueTitle.match(/(\d{2})\D+(\d{4})/);
  if (titleMatch) {
    return {
      title: issueTitle,
      year: titleMatch[2],
      month: titleMatch[1]
    };
  }

  return { title: issueTitle };
}

function getWeb2PrintHtmlContent(item, transformFn) {
  const contentParts = item?.alternative_content?.gemeindeblatt?.length
    ? [item.alternative_content.gemeindeblatt]
    : [item?.content, item?.content_2, item?.content_3].filter(Boolean);

  let html = contentParts
    .map(content => (transformFn ? transformFn(content) : content))
    .join("<br/><br/>");
  html = removeXMLInvalidChars(html);
  html = html.replace(/<br\s*\/?>/gi, "<br/>");
  html = html.replace(/&(?!#\d+;|#x[0-9a-fA-F]+;|[a-zA-Z][\w.-]*;)/g, "&amp;");

  return html;
}

function getWeb2PrintChannelHtmlContent(item, channel, transformFn) {
  const contentParts = [
    item?.alternative_content?.[channel],
    item?.alternative_content_2?.[channel],
    item?.alternative_content_3?.[channel]
  ].filter(Boolean);

  let html = contentParts
    .map(content => (transformFn ? transformFn(content) : content))
    .join("<br/><br/>");
  html = removeXMLInvalidChars(html);
  html = html.replace(/<br\s*\/?>/gi, "<br/>");
  html = html.replace(/&(?!#\d+;|#x[0-9a-fA-F]+;|[a-zA-Z][\w.-]*;)/g, "&amp;");

  return html;
}

function pruneObjectValues(sourceObject) {
  return Object.entries(sourceObject).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});
}

/***
 *
 * @param news
 * @return {[]}
 */
export function getSameNameFiles(news) {
  const uniqueFilenames = [];
  const sameNameFiles = [];

  news.forEach(item => {
    item.images.forEach(img => {
      let filename = this.getFilename(img, ["jpg", "jpeg", "png"]);
      if (uniqueFilenames.includes(filename)) {
        sameNameFiles.push(filename);
      } else {
        uniqueFilenames.push(filename);
      }
    });
  });
  return sameNameFiles;
}
/**
 * generate indesign-xml (not necessary valid xml)
 * @param news
 * @return {Promise<string>}
 */
export async function buildIssueXml(news) {
  const xml = builder.create("root");
  const userCache = [];
  const sameNameFiles = this.getSameNameFiles(news);

  await news.asyncForEach(async item => {
    let user =
      userCache[item.creator_id] ??
      (item.creator_id ? await getUserById(item.creator_id) : "no-user-id");
    userCache[item.creator_id] = user;
    let allRubrikTitles = this.getCategoryForPrintNews(item, true);
    allRubrikTitles = allRubrikTitles?.map(rubrik => rubrik.title).join(" - ");
    const articleTypeString = item?.box_layout ? "Kasten" : "Normal";
    let myContent;
    if (item?.alternative_content?.gemeindeblatt?.length) {
      myContent = this.transformToLegacyXml(
        item.alternative_content.gemeindeblatt
      );
    } else {
      myContent = [item.content, item.content_2, item.content_3]
        .filter(Boolean)
        .map(str => this.transformToLegacyXml(str))
        .join("\n<n></n>\n");
    }
    let alternativeChannels = [];
    if (item.alternative_content)
      alternativeChannels = Object.keys(item.alternative_content);
    alternativeChannels = alternativeChannels.filter(
      channel => channel !== "gemeindeblatt"
    );

    //create xml
    let node = xml
      .ele("article", { articletype: articleTypeString, id: item._id })
      .ele("issue", {}, this.getIssueById(item.issue_id)?.title)
      .up()
      .ele("chapter", {}, allRubrikTitles)
      .up()
      .ele("headline", {}, removeXMLInvalidChars(item.title))
      .up()
      .ele("content")
      .dat(myContent)
      .up();

    if (alternativeChannels?.length) {
      alternativeChannels.forEach(channel => {
        const content = [
          item.alternative_content?.[channel],
          item.alternative_content_2?.[channel],
          item.alternative_content_3?.[channel]
        ]
          .filter(Boolean)
          .map(str => this.transformToLegacyXml(str))
          .join("\n<n></n>\n");
        if (content?.length) {
          node.ele(`alternative-${channel}`).dat(content);
        }
      });
    }

    node.ele("user", {}, user?.username ?? "no-user-id");

    if (item.short_title) {
      node.ele("subline", {}, removeXMLInvalidChars(item.short_title)).up();
    }
    if (item.production_note) {
      node.ele("info", {}, item.production_note).up();
    }

    if (item.images) {
      node = node.ele("images");
      item.images.forEach((img, index) => {
        let filename = this.getFilename(img, ["jpg", "jpeg", "png"]);
        if (sameNameFiles.includes(filename)) {
          filename =
            (img._id || img.id || item._id + "_" + index.toString()) +
            "_" +
            filename;
        }
        let imageSubline = img.alternative_title
          ? img.alternative_title
          : img.title;
        imageSubline = removeXMLInvalidChars(imageSubline);
        if (imageSubline && img.copyright) {
          imageSubline += " — Copyright: " + img.copyright;
        }
        if (!imageSubline && img.copyright) {
          imageSubline = "Copyright: " + img.copyright;
        }
        if (!imageSubline) imageSubline = "??";
        node.ele("image", { href: filename }, imageSubline);
      });
    }
  });
  //remove CDATA-sections (no indesign-xml support)
  return xml
    .end({ pretty: true })
    .toString()
    .replace(/<!\[CDATA\[/g, "")
    .replace(/]]>/g, "")
    .replace(/]]>/g, "");
}

export async function buildLegacyIssueXml(news) {
  const xml = builder.create("root");
  xml.com("Legacy Export");
  const userCache = [];
  const sameNameFiles = this.getSameNameFiles(news);

  await news.asyncForEach(async item => {
    let user =
      userCache[item.creator_id] ??
      (item.creator_id ? await getUserById(item.creator_id) : "no-user-id");
    userCache[item.creator_id] = user;
    let allRubrikTitles = this.getCategoryForPrintNews(item, true);
    allRubrikTitles = allRubrikTitles?.map(rubrik => rubrik.title).join(" - ");
    const articleTypeString = item?.box_layout ? "Kasten" : "Normal";
    let myContent;
    if (item?.alternative_content?.gemeindeblatt?.length) {
      myContent = this.transformToLegacyXml(
        item.alternative_content.gemeindeblatt
      );
    } else {
      myContent = [item.content, item.content_2, item.content_3]
        .filter(Boolean)
        .map(str => this.transformToLegacyXml(str))
        .join("\n<n></n>\n");
    }
    let alternativeChannels = [];
    if (item.alternative_content)
      alternativeChannels = Object.keys(item.alternative_content);
    alternativeChannels = alternativeChannels.filter(
      channel => channel !== "gemeindeblatt"
    );

    if (item?.author) {
      if (myContent.trim().endsWith("</n>")) {
        myContent = myContent.trim().slice(0, -4);
        myContent += ` Von: ${item.author}</n>`;
      } else {
        myContent += `<n>Von: ${item.author}</n>`;
      }
    }

    //create xml
    let node = xml
      .ele("article", { articletype: articleTypeString, id: item._id })
      .ele("issue", {}, this.getIssueById(item.issue_id)?.title)
      .up()
      .ele("chapter", {}, allRubrikTitles)
      .up()
      .ele("headline", {}, removeXMLInvalidChars(item.title))
      .up()
      .ele("content")
      .dat(myContent)
      .up();

    if (alternativeChannels?.length) {
      alternativeChannels.forEach(channel => {
        const content = [
          item.alternative_content?.[channel],
          item.alternative_content_2?.[channel],
          item.alternative_content_3?.[channel]
        ]
          .filter(Boolean)
          .map(str => this.transformToLegacyXml(str))
          .join("\n<n></n>\n");
        if (content?.length) {
          node.ele(`alternative-${channel}`).dat(content);
        }
      });
    }

    node.ele("user", {}, user?.username ?? "no-user-id");

    if (item.short_title) {
      node.ele("subline", {}, removeXMLInvalidChars(item.short_title)).up();
    }
    if (item.production_note) {
      node.ele("info", {}, item.production_note).up();
    }

    if (item.images) {
      node = node.ele("images");
      item.images.forEach((img, index) => {
        let filename = this.getFilename(img, ["jpg", "jpeg", "png"]);
        if (sameNameFiles.includes(filename)) {
          filename =
            (img._id || img.id || item._id + "_" + index.toString()) +
            "_" +
            filename;
        }
        let imageSubline = img.alternative_title
          ? img.alternative_title
          : img.title;
        imageSubline = removeXMLInvalidChars(imageSubline);
        if (imageSubline && img.copyright) {
          imageSubline += " — Copyright: " + img.copyright;
        }
        if (!imageSubline && img.copyright) {
          imageSubline = "Copyright: " + img.copyright;
        }
        if (!imageSubline) imageSubline = "??";
        node.ele("image", { href: filename }, imageSubline);
      });
    }
  });
  //remove CDATA-sections (no indesign-xml support)
  return xml
    .end({ pretty: true })
    .toString()
    .replace(/<!\[CDATA\[/g, "")
    .replace(/]]>/g, "")
    .replace(/]]>/g, "");
}

// Build WebToPrint XML schema as far as available News data allows.
export async function buildWebToPrintIssueXml(news, pois) {
  const xml = builder.create("root");
  xml.att("schema", "2.0");
  xml.com("Web2Print Export");
  const userCache = [];
  const sameNameFiles = this.getSameNameFiles(news);

  await news.asyncForEach(async item => {
    let user =
      userCache[item.creator_id] ??
      (item.creator_id ? await getUserById(item.creator_id) : "no-user-id");
    userCache[item.creator_id] = user;
    let categories = [];
    try {
      categories = this.getCategoryForPrintNews(item, true) || [];
    } catch (err) {
      categories = [];
    }

    const chapterTitle = removeXMLInvalidChars(
      categories
        ?.map(category => category?.title)
        .filter(Boolean)
        .join(" - ") || ""
    );
    const issue = this.getIssueById(item.issue_id) || {};
    const issueMeta = getIssueMeta(
      issue,
      item?.newsDate || item?.web_release_date
    );
    const contentHtml = getWeb2PrintHtmlContent(item, content =>
      this.transformToWebToPrintXml(content)
    );
    const articleAttributes = pruneObjectValues({
      id: item?._id,
      articletype: item?.articleType?.gemeindeblatt,
      priority: WEB2PRINT_PRIORITY_MAPPING[item?.priority],
      status: item?.released ? "freigegeben" : "entwurf",
      changed: toIsoUtcString(item?.change_date || item?.create_date)
    });

    const articleNode = xml.ele("article", articleAttributes);
    articleNode.ele(
      "issue",
      pruneObjectValues({
        year: issueMeta.year,
        month: issueMeta.month
      }),
      removeXMLInvalidChars(issueMeta.title || "")
    );
    articleNode.ele(
      "chapter",
      pruneObjectValues({
        key: slugifyKey(chapterTitle)
      }),
      chapterTitle
    );
    articleNode.ele("topic", {}, chapterTitle.toUpperCase());
    articleNode.ele("headline", {}, removeXMLInvalidChars(item?.title || ""));

    if (item?.short_title) {
      articleNode.ele("subline", {}, removeXMLInvalidChars(item.short_title));
    }
    if (item?.teaser) {
      articleNode.ele(
        "teaser",
        {},
        removeXMLInvalidChars(toPlainText(item.teaser))
      );
    }
    if (item?.author) {
      articleNode.ele("author", {}, removeXMLInvalidChars(item.author));
    }

    if (item.printIntroductionText?.length) {
      articleNode.ele("introduction").dat(item.printIntroductionText);
    }

    articleNode
      .ele("content")
      .dat(contentHtml)
      .up();
    articleNode.ele("user", {}, user?.username ?? "no-user-id");

    let alternativeChannels = [];
    if (item.alternative_content) {
      alternativeChannels = Object.keys(item.alternative_content);
    }
    alternativeChannels = alternativeChannels.filter(
      channel => channel !== "gemeindeblatt"
    );
    if (alternativeChannels?.length) {
      alternativeChannels.forEach(channel => {
        const content = getWeb2PrintChannelHtmlContent(item, channel, content =>
          this.transformToWebToPrintXml(content)
        );
        if (content?.length) {
          articleNode.ele(`alternative-${channel}`).dat(content);
        }
      });
    }

    const poiIds = Array.isArray(item?.pois) ? item.pois : [];
    if (poiIds.length) {
      let poisNode;
      for (const poiId of poiIds) {
        const poi = pois.find(_poi => _poi._id === poiId);
        if (!poi) {
          continue;
        }
        if (!poisNode) {
          poisNode = articleNode.ele("pois");
        }
        const poiNode = poisNode.ele("poi");
        poiNode.ele("title", {}, removeXMLInvalidChars(poi.title || ""));
        if (poi.logo) {
          poiNode.ele("logo", { href: removeXMLInvalidChars(poi.logo || "") });
        }
      }
    }

    if (Array.isArray(item?.images) && item.images.length) {
      const imagesNode = articleNode.ele("images");
      item.images.forEach((img, index) => {
        let filename = this.getFilename(img, ["jpg", "jpeg", "png"]);
        if (sameNameFiles.includes(filename)) {
          filename =
            (img._id || img.id || `${item._id}_${index.toString()}`) +
            "_" +
            filename;
        }

        const imageNode = imagesNode.ele("image", {
          id: `img-${index + 1}`,
          href: filename,
          rank: String(index + 1)
        });
        imageNode.ele(
          "caption",
          {},
          removeXMLInvalidChars(img?.alternative_title || img?.title || "")
        );
        imageNode.ele(
          "credit",
          {},
          removeXMLInvalidChars(img?.copyright || "")
        );
      });
    }

    if (item?.production_note) {
      articleNode.ele(
        "production_note",
        {},
        removeXMLInvalidChars(item.production_note)
      );
    }
  });

  return xml
    .end({ pretty: true })
    .toString()
    .replace(/<!\[CDATA\[/g, "")
    .replace(/]]>/g, "")
    .replace(/]]>/g, "");
}

// get filename of path
export function basename(path) {
  return path.split("/").reverse()[0];
}

export function sortByCategory(news) {
  news.map(item => {
    try {
      const feature = this.getCategoryForPrintNews(item);
      item.sort = Number(feature.sort);
    } catch (e) {
      item.sort = 999;
    }
    return item;
  });

  return news.sort(function(a, b) {
    if (a.sort < b.sort) {
      return -1;
    }
    if (a.sort > b.sort) {
      return 1;
    }
    return 0;
  });
}

/**
 * transforms html to content-tag for indesign-xml
 * @param content
 * @return string
 */
/**
 * transforms html to content-tag for legacy xml
 * @param content
 * @return string
 */
export function transformToLegacyXml(content) {
  if (!content) {
    return "";
  }

  // remove html-attributes
  content = content.replace(/<\s*(\w+).*?>/g, "<$1>");

  // remove double spaces
  content = content.replace(/ +/g, " ");
  // remove p-tags with only one no-breaking-space
  content = content.replace(/<p>&nbsp;<\/p>/g, "");
  content = content.replace(/<\/p>\s*<p>/g, "<br>");

  // remove some tags
  content = content.replace(/<p>/g, "");
  content = content.replace(/<\/p>/g, "");

  // This regex matches any <br> tag, including variations like <br>, <br/>, and <br />.
  // - `<br` matches the opening of the tag.
  // - `\s*` matches any optional whitespace after <br.
  // - `\/?` matches an optional forward slash (for self-closing tags).
  // - `>` matches the closing of the tag.
  content = content.replace(/<br\s*\/?>\n/g, "\n");
  content = content.replace(/<br\s*\/?>/g, "\n");

  // strong to b, em to i
  content = content.replace(/<strong>/g, "<b>");
  content = content.replace(/<\/strong>/g, "</b>");
  content = content.replace(/<em>/g, "<i>");
  content = content.replace(/<\/em>/g, "</i>");

  // replace some chars to avoid convertion
  content = content.replace(/&lt;/g, "[lt]");
  content = content.replace(/&gt;/g, "[gt]");
  content = content.replace(/&apos;/g, "[apos]");
  content = content.replace(/&quot;/g, "[quot]");

  // remove no-breaking-space indention (double &nbsp;)
  content = content.replace(/(&nbsp;)+/g, "&nbsp;");
  //nbsp to space
  content = content.replace(/&nbsp;/g, " ");

  content = this.htmlentitiesDecode(content);
  content = this.filterDeepTags(content);
  content = this.tagUntagged(content);
  content = this.htmlentitiesDecode(content);

  content = content.replace(/<n>\n<\/n>/g, "<br/>");

  // convert chars back. ATTENTION keep this sequence
  content = content.replace(/&/g, "&amp;");
  content = content.replace(/<br>/g, "<br/>");
  content = content.replace(/\[lt]/g, "&lt;");
  content = content.replace(/\[gt]/g, "&gt;");
  content = content.replace(/\[apos]/g, "&apos;");
  content = content.replace(/\[quot]/g, "&quot;");

  return removeXMLInvalidChars(content);
}

/**
 * transforms html to content-tag for web-to-print xml
 * @param content
 * @return string
 */
export function transformToWebToPrintXml(content) {
  if (!content) {
    return "";
  }

  // remove double spaces
  content = content.replace(/ +/g, " ");
  // remove p-tags with only one no-breaking-space
  content = content.replace(/<p>&nbsp;<\/p>/g, "");

  // replace some chars to avoid convertion
  content = content.replace(/&lt;/g, "[lt]");
  content = content.replace(/&gt;/g, "[gt]");
  content = content.replace(/&apos;/g, "[apos]");
  content = content.replace(/&quot;/g, "[quot]");

  // remove no-breaking-space indention (double &nbsp;)
  content = content.replace(/(&nbsp;)+/g, "&nbsp;");
  //nbsp to space
  content = content.replace(/&nbsp;/g, " ");

  content = this.htmlentitiesDecode(content);

  // convert chars back. ATTENTION keep this sequence
  content = content.replace(/&/g, "&amp;");
  content = content.replace(/<br>/g, "<br/>");
  content = content.replace(/\[lt]/g, "&lt;");
  content = content.replace(/\[gt]/g, "&gt;");
  content = content.replace(/\[apos]/g, "&apos;");
  content = content.replace(/\[quot]/g, "&quot;");

  return removeXMLInvalidChars(content);
}

/**
 * Remove tags inside tags.
 * Example: <b><i>Test</i></b> should output: <b>Test</b>
 * @param content
 * @return string
 */
export function filterDeepTags(content) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    "<root><subroot>" + content + "</subroot></root>",
    "text/html"
  );
  const root = doc.querySelector("root");
  //select only the first level of b-tags
  root.querySelectorAll("subroot>b").forEach(b => {
    b.innerHTML = b.textContent;
  });
  //select only the first level of i-tags
  root.querySelectorAll("subroot>i").forEach(i => {
    i.innerHTML = i.textContent;
  });

  return root.querySelector("subroot").innerHTML;
}

/**
 * decodes htmlentities
 * @param content
 * @return string
 */
export function htmlentitiesDecode(content) {
  const entities = new XmlEntities();
  return entities.decode(content);
}

/**
 * surround untagged text with n-tag.
 * Example: 'my example text <b>is awesome</b>'
 * should output: '<n>my example text </n><b>is awesome</b>'
 * @param content
 * @return string
 */
export function tagUntagged(content) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${content}</root>`, "text/html");
  const root = doc.querySelector("root");

  Array.from(root.childNodes).forEach(node => {
    if (node.nodeType !== 3) {
      return;
    }
    if (!node.textContent?.trim().length) {
      return;
    }

    const nNode = doc.createElement("n");
    nNode.textContent = node.textContent;
    root.replaceChild(nNode, node);
  });

  return root.innerHTML;
}

/**
 * convert filename to clean maintainable form
 * z.B. "Wirtschaft spendet AHF Spendenaktion LKR - ZAK u.a. 31-01-2019-hpg 1464 (1).jpg"
 * in "wirtschaft-spendet-ahf-spendenaktion-lkr-zak-ua-31-01-2019-hpg-1464-1.jpg"
 * @param fileName
 * @return string
 */
export function slugifyFilename(fileName) {
  const fileSplit = fileName.split(".");
  let fileExtension = fileSplit[fileSplit.length - 1];

  if (fileSplit.length === 1 || fileExtension.length > 9) {
    // fileExtension seems invalid use fallback file-extension
    fileExtension = "jpg";
  }
  fileSplit.pop();
  let part1 = fileSplit.join(".");

  const a = "àáäâãåăæçèéëêǵḧìíïîḿńǹñòóöôœṕŕßśșțùúüûǘẃẍÿź·/_,:;";
  const b = "aaaaaaaaceeeeghiiiimnnnoooooprssstuuuuuwxyz------";
  const p = new RegExp(a.split("").join("|"), "g");

  part1 = part1
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/&/g, "-and-") // Replace & with 'and'
    .replace(/[^\w-]+/g, "") // Remove all non-word characters
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text

  if (!part1) {
    part1 = "leer";
  }

  return part1 + "." + fileExtension;
}

export function getFilename(image, imgTypes) {
  let filename;
  try {
    filename = this.slugifyFilename(this.basename(image.url));
    if (filename && filename.split(".")[0].match(/^[0-9a-fA-F]{24}$/)) {
      filename = this.slugifyFilename(
        image.original_name || image.title || this.basename(image.url)
      );
      const filenameParts = filename ? filename.split(".") : [];
      const suffix = filenameParts[filenameParts.length - 1];
      if (!imgTypes.includes(suffix)) {
        const urlParts = image.url ? image?.url?.split(".") : [];
        const urlSuffix = urlParts[urlParts.length - 1];
        if (
          urlSuffix &&
          urlSuffix !== "undefined" &&
          !filename.includes(`.${urlSuffix}`)
        ) {
          filename += urlSuffix ? `.${urlSuffix}` : "";
        }
      }
      //fallback f.e. legacy-files saved without original_name
      if (filename.split(".")[1] === "undefined") {
        filename = filename.replace(".undefined", ".evtl.jpg");
      }
    }
  } catch (err) {
    console.info("Error getting Basename... using title", err.message);
  }
  filename = filename.replace("leer.", image._id + ".");
  return filename.toLowerCase();
}

/**
 * Removes XML-invalid characters from a string.
 * https://www.ryadel.com/en/javascript-remove-xml-invalid-chars-characters-string-utf8-unicode-regex/
 * @param {string} string - a string potentially containing XML-invalid characters, such as non-UTF8 characters, STX, EOX and so on.
 * @param {boolean} removeDiscouragedChars - a string potentially containing XML-invalid characters, such as non-UTF8 characters, STX, EOX and so on.
 * @return : a sanitized string without all the XML-invalid characters.
 */
export function removeXMLInvalidChars(string, removeDiscouragedChars = true) {
  if (!string?.length) {
    return "";
  }
  // remove everything forbidden by XML 1.0 specifications, plus the unicode replacement character U+FFFD
  // eslint-disable-next-line no-control-regex
  var regex = /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g;
  string = string.replace(regex, "");
  // remove STX and EOX (start and end of text) characters
  // eslint-disable-next-line no-control-regex
  string = string.replace(/[\x02\x03]/g, "");

  if (removeDiscouragedChars) {
    // remove everything not suggested by XML 1.0 specifications
    regex = new RegExp(
      "([\\x7F-\\x84]|[\\x86-\\x9F]|[\\uFDD0-\\uFDEF]|(?:\\uD83F[\\uDFFE\\uDFFF])|(?:\\uD87F[\\uDF" +
        "FE\\uDFFF])|(?:\\uD8BF[\\uDFFE\\uDFFF])|(?:\\uD8FF[\\uDFFE\\uDFFF])|(?:\\uD93F[\\uDFFE\\uD" +
        "FFF])|(?:\\uD97F[\\uDFFE\\uDFFF])|(?:\\uD9BF[\\uDFFE\\uDFFF])|(?:\\uD9FF[\\uDFFE\\uDFFF])" +
        "|(?:\\uDA3F[\\uDFFE\\uDFFF])|(?:\\uDA7F[\\uDFFE\\uDFFF])|(?:\\uDABF[\\uDFFE\\uDFFF])|(?:\\" +
        "uDAFF[\\uDFFE\\uDFFF])|(?:\\uDB3F[\\uDFFE\\uDFFF])|(?:\\uDB7F[\\uDFFE\\uDFFF])|(?:\\uDBBF" +
        "[\\uDFFE\\uDFFF])|(?:\\uDBFF[\\uDFFE\\uDFFF])(?:[\\0-\\t\\x0B\\f\\x0E-\\u2027\\u202A-\\uD7FF\\" +
        "uE000-\\uFFFF]|[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]|[\\uD800-\\uDBFF](?![\\uDC00-\\uDFFF])|" +
        "(?:[^\\uD800-\\uDBFF]|^)[\\uDC00-\\uDFFF]))",
      "g"
    );
    string = string.replace(regex, "");
  }

  return string;
}
